import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { logClick, logInfo } from './utils/logger';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Problem from './components/Problem';
import Solution from './components/Solution';
import Features from './components/Features';
import DemoVideo from './components/DemoVideo';
import WhyChoose from './components/WhyChoose';
import Products from './components/Products';
import Services from './components/Services';
import Pricing from './components/Pricing';
import UseCases from './components/UseCases';
import Security from './components/Security';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import CTA from './components/CTA';
import Footer from './components/Footer';
import DebugLogger from './components/DebugLogger';
import ChatWidget from './components/ChatWidget';
import CookieConsent from './components/CookieConsent';

// Pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import Compliance from './pages/Compliance';

// Landing page component with scroll logic
function LandingPage({ section }) {
  React.useEffect(() => {
    if (section) {
      const element = document.getElementById(section);
      if (element) {
        setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } else if (window.location.hash) {
      // Fallback for hash links
      const id = window.location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [section]);

  return (
    <>
      <Hero />
      <div id="problem"><Problem /></div>
      <div id="solution"><Solution /></div>
      <div id="features"><Features /></div>
      <div id="demo"><DemoVideo /></div>
      <div id="why-choose"><WhyChoose /></div>
      <div id="products"><Products /></div>
      <div id="services"><Services /></div>
      <div id="pricing"><Pricing /></div>
      <div id="use-cases"><UseCases /></div>
      <div id="security"><Security /></div>
      <div id="faq"><FAQ /></div>
      <div id="contact"><Contact /></div>
      <CTA />
      <Footer />
    </>
  );
}

function App() {
  // Global click tracking
  useEffect(() => {
    const handleClick = (event) => {
      const target = event.target;
      const tagName = target.tagName?.toLowerCase();

      // Log clicks on interactive elements
      if (
        ['button', 'a', 'input', 'select', 'textarea'].includes(tagName) ||
        target.getAttribute('role') === 'button' ||
        target.onclick ||
        target.className?.includes('button') ||
        target.className?.includes('btn')
      ) {
        logClick(target, {
          page: window.location.pathname,
          href: target.href,
          type: target.type,
          name: target.name
        });
      }
    };

    // Add click listener
    document.addEventListener('click', handleClick, true);
    logInfo('Click tracking initialized');

    // Cleanup
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, []);

  return (
    <Router>
      <DebugLogger />
      <Navbar />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/contact" element={<LandingPage section="contact" />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/privacy-policy" element={<><PrivacyPolicy /><Footer /></>} />
        <Route path="/terms-of-service" element={<><TermsOfService /><Footer /></>} />
        <Route path="/cookie-policy" element={<><CookiePolicy /><Footer /></>} />
        <Route path="/compliance" element={<><Compliance /><Footer /></>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ChatWidget />
      <CookieConsent />
    </Router>
  );
}

export default App;
